import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import type { EditorTool, SceneObject } from '../../../types';
import { buildShapeFromDrawing } from './buildShapeFromDrawing';
import { DrawingPreview } from './DrawingPreview';
import {
  INITIAL_DRAWING_STATE,
  isShapeDrawingTool,
  type DrawingState,
} from './types';

type PointerLike = { clientX: number; clientY: number };

export type UseStageDrawingOptions = {
  currentTool: EditorTool;
  getStageCoordinates: (e: PointerLike) => { x: number; y: number } | null;
  isPointerInDrawingWorkspace: (e: PointerLike, target: EventTarget | null) => boolean;
  snapValue: (value: number) => number;
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  strokeStyle: NonNullable<SceneObject['strokeStyle']>;
  objectCount: number;
  persistShapeObject: (newObject: SceneObject) => Promise<SceneObject | null>;
  addSceneObject: (object: SceneObject) => void;
  select: (id: string) => void;
};

export function useStageDrawing({
  currentTool,
  getStageCoordinates,
  isPointerInDrawingWorkspace,
  snapValue,
  fillColor,
  strokeColor,
  strokeWidth,
  strokeStyle,
  objectCount,
  persistShapeObject,
  addSceneObject,
  select,
}: UseStageDrawingOptions) {
  const [drawingState, setDrawingState] = useState<DrawingState>(INITIAL_DRAWING_STATE);
  const drawingStateRef = useRef<DrawingState>(INITIAL_DRAWING_STATE);
  const drawingSessionRef = useRef<{
    pointerId: number;
    captureTarget: Element;
    onMove: (e: Event) => void;
    onUp: (e: Event) => void;
  } | null>(null);

  const styleRef = useRef({
    snapValue,
    fillColor,
    strokeColor,
    strokeWidth,
    strokeStyle,
    objectCount,
    persistShapeObject,
    addSceneObject,
    select,
    getStageCoordinates,
  });
  styleRef.current = {
    snapValue,
    fillColor,
    strokeColor,
    strokeWidth,
    strokeStyle,
    objectCount,
    persistShapeObject,
    addSceneObject,
    select,
    getStageCoordinates,
  };

  const setDrawing = useCallback((next: DrawingState) => {
    drawingStateRef.current = next;
    setDrawingState(next);
  }, []);

  const endDrawingSession = useCallback(() => {
    const session = drawingSessionRef.current;
    if (!session) return;

    session.captureTarget.removeEventListener('pointermove', session.onMove);
    session.captureTarget.removeEventListener('pointerup', session.onUp);
    session.captureTarget.removeEventListener('pointercancel', session.onUp);
    window.removeEventListener('pointermove', session.onMove);
    window.removeEventListener('pointerup', session.onUp);
    window.removeEventListener('pointercancel', session.onUp);

    try {
      if (session.captureTarget.hasPointerCapture(session.pointerId)) {
        session.captureTarget.releasePointerCapture(session.pointerId);
      }
    } catch {
      // ignore
    }
    drawingSessionRef.current = null;
  }, []);

  const finalizeDrawing = useCallback(
    (ev: PointerEvent) => {
      if (!drawingSessionRef.current) return;

      const prev = drawingStateRef.current;
      if (!prev.isDrawing || !prev.tool) {
        endDrawingSession();
        setDrawing(INITIAL_DRAWING_STATE);
        return;
      }

      const finalCoords = styleRef.current.getStageCoordinates(ev);
      const finalState: DrawingState = finalCoords
        ? { ...prev, currentX: finalCoords.x, currentY: finalCoords.y }
        : prev;

      const newObject = buildShapeFromDrawing(finalState, {
        snapValue: styleRef.current.snapValue,
        fillColor: styleRef.current.fillColor,
        strokeColor: styleRef.current.strokeColor,
        strokeWidth: styleRef.current.strokeWidth,
        strokeStyle: styleRef.current.strokeStyle,
        zIndex: styleRef.current.objectCount,
      });

      setDrawing(INITIAL_DRAWING_STATE);
      endDrawingSession();

      if (!newObject) return;

      void styleRef.current
        .persistShapeObject(newObject)
        .then((createdObject) => {
          if (!createdObject) return;
          styleRef.current.addSceneObject(createdObject);
          styleRef.current.select(createdObject.id);
        })
        .catch((error) => {
          console.error('[useStageDrawing] Failed to persist drawn scene object:', error);
        });
    },
    [endDrawingSession, setDrawing]
  );

  const isShapeDrawingToolSelected = isShapeDrawingTool(currentTool);

  const handleDrawingMouseDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      if (!isShapeDrawingTool(currentTool)) return;
      if (!isPointerInDrawingWorkspace(e, e.target)) return;
      if (drawingSessionRef.current) return;

      const coords = getStageCoordinates(e);
      if (!coords) return;

      e.preventDefault();
      e.stopPropagation();

      const initialState: DrawingState = {
        isDrawing: true,
        startX: coords.x,
        startY: coords.y,
        currentX: coords.x,
        currentY: coords.y,
        tool: currentTool,
      };
      setDrawing(initialState);

      const captureTarget = e.currentTarget;
      try {
        captureTarget.setPointerCapture(e.pointerId);
      } catch {
        // window fallback listeners still handle the gesture
      }

      const onMove = (event: Event) => {
        const ev = event as PointerEvent;
        if (ev.pointerId !== e.pointerId) return;
        const moveCoords = styleRef.current.getStageCoordinates(ev);
        if (!moveCoords) return;

        const prev = drawingStateRef.current;
        if (!prev.isDrawing) return;

        const next: DrawingState = {
          ...prev,
          currentX: moveCoords.x,
          currentY: moveCoords.y,
        };
        if (prev.tool === 'curve' && prev.controlX == null) {
          const dx = moveCoords.x - prev.startX;
          const dy = moveCoords.y - prev.startY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist >= 20) {
            next.controlX = moveCoords.x;
            next.controlY = moveCoords.y;
          }
        }
        setDrawing(next);
      };

      const onUp = (event: Event) => {
        const ev = event as PointerEvent;
        if (ev.pointerId !== e.pointerId) return;
        finalizeDrawing(ev);
      };

      drawingSessionRef.current = {
        pointerId: e.pointerId,
        captureTarget,
        onMove,
        onUp,
      };

      captureTarget.addEventListener('pointermove', onMove);
      captureTarget.addEventListener('pointerup', onUp);
      captureTarget.addEventListener('pointercancel', onUp);
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [
      currentTool,
      getStageCoordinates,
      isPointerInDrawingWorkspace,
      finalizeDrawing,
      setDrawing,
    ]
  );

  const drawingPreview: ReactNode = useMemo(
    () => (
      <DrawingPreview
        state={drawingState}
        fillColor={fillColor}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        strokeStyle={strokeStyle}
      />
    ),
    [drawingState, fillColor, strokeColor, strokeWidth, strokeStyle]
  );

  return {
    drawingState,
    isDrawingActive: drawingState.isDrawing,
    isShapeDrawingToolSelected,
    handleDrawingMouseDown,
    drawingPreview,
  };
}
